import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export interface FutureLetter {
  id: string;
  writtenOn: string;
  openOn: string;
  opened: boolean;
  /** only once its day has come */
  body: string | null;
}

/** The student's letters to the future; sealed ones come without their text. */
export function useFutureLetters() {
  const db = useDb();
  return useQuery({
    queryKey: ['future-letters'],
    queryFn: async (): Promise<FutureLetter[]> =>
      must(await db.rpc('my_future_letters')).map((l) => ({
        id: l.id,
        writtenOn: l.written_on,
        openOn: l.open_on,
        opened: l.opened,
        body: l.body,
      })),
  });
}

/** When a letter may open: in a year, at the end of the school year, at the graduation. */
export function letterDates(today: string, grade: number | undefined) {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const schoolYearEnds = month >= 9 ? year + 1 : year;
  const iso = (y: number, md: string) => `${y}-${md}`;
  return {
    nextYear: iso(year + 1, today.slice(5)),
    yearEnd: iso(schoolYearEnds, '06-30'),
    graduation: iso(schoolYearEnds + Math.max(0, 11 - (grade ?? 11)), '06-30'),
  };
}

export function useLetterActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['future-letters'] });
  return {
    write: useMutation({
      mutationFn: async (a: { schoolId: string; body: string; openOn: string }) =>
        must(
          await db.rpc('write_future_letter', {
            p_school: a.schoolId,
            p_body: a.body,
            p_open_on: a.openOn,
          }),
        ),
      onSettled: invalidate,
    }),
    discard: useMutation({
      mutationFn: async (id: string) =>
        must(await db.rpc('discard_future_letter', { p_letter: id })),
      onSettled: invalidate,
    }),
  };
}
